import { Users, Rocket, Laptop, Megaphone, Headphones, ChevronRight, Mail } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Careers() {
  usePageMeta(
    "Careers at Shattara AI — Join Our Team",
    "Join the Shattara AI team and help build the future of AI-powered education for students across Saudi Arabia and the Arab world."
  );
  const applicationFormUrl = "https://tally.so/r/b55701";

  const teams = [
    {
      id: "engineering",
      icon: <Laptop className="h-5 w-5 text-primary" />,
      title: "Engineering",
      description: "Help us build the AI learning experience for students across Saudi Arabia and the Arab world.",
    },
    {
      id: "marketing",
      icon: <Megaphone className="h-5 w-5 text-primary" />,
      title: "Marketing & Growth",
      description: "Grow the Shattara community through authentic storytelling and student-focused campaigns.",
    },
    {
      id: "support",
      icon: <Headphones className="h-5 w-5 text-primary" />,
      title: "Customer Support / Student Success",
      description: "Support learners with empathy and cultural understanding in Arabic and English.",
    },
  ];

  const upcomingRoles = [
    {
      role: "Full-Stack Software Engineer (React + Supabase)",
      type: "Full-time",
      location: "Remote / Saudi Arabia",
    },
    {
      role: "AI/ML Engineer (LLMs, RAG, Arabic Embeddings)",
      type: "Full-time",
      location: "Remote / Saudi Arabia",
    },
    {
      role: "Growth & Community Manager (Students & Universities)",
      type: "Full-time",
      location: "Remote / Saudi Arabia",
    },
    {
      role: "Customer Support Specialist (Arabic/English)",
      type: "Full/Part-time",
      location: "Remote / Saudi Arabia",
    },
  ];

  const openApplication = () => {
    window.open(applicationFormUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <main className="flex-grow">
        <div className="mx-auto max-w-6xl px-4 py-14 space-y-20">
          {/* Hero */}
          <section className="space-y-8 text-center max-w-3xl mx-auto">
            {/* Label */}
            <div className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-1 text-xs font-medium text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              Careers
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
              Help build the future of learning for millions of students
            </h1>

            {/* Description */}
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              We're building an AI-powered study companion created for students in our region — in our language, in our
              culture, and for our future. If that mission excites you, we'd love to meet you.
            </p>

            {/* CTA */}
            <div className="pt-2">
              <button
                onClick={openApplication}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition"
              >
                Join Our Talent Network
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </section>

          {/* Our mission */}
          <section className="grid gap-12 md:grid-cols-2 items-start">
            {/* Left — Mission */}
            <div className="space-y-5 max-w-lg">
              <div className="flex items-center gap-2 text-primary">
                <Rocket className="h-5 w-5" />
                <span className="text-sm font-medium">Our Mission</span>
              </div>

              <h2 className="text-3xl font-semibold tracking-tight">Why Shattara AI exists</h2>

              <p className="text-muted-foreground leading-relaxed">
                Shattara AI is here to help students study smarter, understand faster, and learn with confidence. We
                believe generative AI should empower students — not replace their effort — while respecting our Arabic
                language and cultural identity.
              </p>
            </div>

            {/* Right — Values */}
            <div className="border rounded-xl bg-card p-6">
              <h3 className="font-semibold mb-5">Our Values</h3>

              <ul className="space-y-4 text-sm text-muted-foreground">
                <li>
                  <span className="font-medium text-foreground">🤝 Students First</span>
                  <p>Every decision starts with improving a learner's life.</p>
                </li>

                <li>
                  <span className="font-medium text-foreground">🌱 Growth Mindset</span>
                  <p>We experiment, learn, and improve together.</p>
                </li>

                <li>
                  <span className="font-medium text-foreground">🕌 Respect & Integrity</span>
                  <p>Our work reflects our culture and purpose.</p>
                </li>

                <li>
                  <span className="font-medium text-foreground">🚀 Ownership</span>
                  <p>Freedom with responsibility, not micromanagement.</p>
                </li>

                <li>
                  <span className="font-medium text-foreground">❤️ Kindness & Trust</span>
                  <p>We treat each other with empathy and care.</p>
                </li>
              </ul>
            </div>
          </section>

          {/* Teams */}
          <section className="space-y-10">
            {/* Header */}
            <div className="max-w-xl">
              <h2 className="text-2xl font-semibold tracking-tight">Teams We Are Growing</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Join us in building the future of AI-powered learning for students across Saudi Arabia and the Arab
                world.
              </p>
            </div>

            {/* Teams Grid */}
            <div className="grid gap-6 md:grid-cols-3">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className="group border rounded-xl p-6 bg-card hover:bg-muted/40 hover:shadow-sm transition"
                >
                  {/* Icon */}
                  <div className="mb-4 text-primary">{team.icon}</div>

                  {/* Title */}
                  <h3 className="font-semibold text-base mb-2">{team.title}</h3>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground leading-relaxed">{team.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Roles */}
          <section className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold tracking-tight">Upcoming Roles</h2>

              <span className="text-sm text-muted-foreground">Hiring Soon</span>
            </div>

            <div className="divide-y border rounded-xl bg-card">
              {upcomingRoles.map((r, index) => (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-5 hover:bg-muted/40 transition cursor-pointer"
                >
                  {/* Role */}
                  <div className="font-medium text-sm">{r.role}</div>

                  {/* Meta */}
                  <div className="flex gap-4 text-xs text-muted-foreground mt-2 sm:mt-0">
                    <span>{r.type}</span>
                    <span>{r.location}</span>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={openApplication}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition"
            >
              Apply Early
              <ChevronRight className="h-4 w-4" />
            </button>
          </section>

          {/* General Application */}
          <section className="border rounded-2xl bg-gradient-to-r from-card to-muted/40 p-6 md:p-10">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              {/* Left - Illustration */}
              <div className="flex items-center justify-center h-full">
                <img
                  src="/assets/campus-ambassador.png"
                  alt="Shattara AI Campus Ambassador"
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Right - Content */}
              <div className="space-y-6 text-center md:text-left">
                <p className="text-sm italic text-muted-foreground">we are hiring...</p>

                <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Become a Campus Ambassador</h2>

                <p className="text-muted-foreground max-w-md">
                  Represent <span className="font-bold">Shattara AI</span> at your university. Help students discover
                  smarter ways to study with AI while gaining leadership experience and building your network.
                </p>

                {/* Perks */}
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>🎓 Leadership experience</li>
                  <li>🚀 Early access to new Shattara AI features</li>
                  <li>🌍 Build your network with ambitious students</li>
                  <li>🏆 Exclusive ambassador rewards & recognition</li>
                </ul>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2 justify-center md:justify-start">
                  <a
                    href="https://tally.so/r/ZjdM2z"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition"
                  >
                    Apply Now
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
